/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { format } from 'date-fns';
import queryString from 'query-string';

import { APIClients, API_PATHS, interpolatePathParams, ROUTE_PARAMS } from '@futrli/shared-services';
import {
  ChartOfAccountBulkFlatItem,
  Consolidation,
  FxOverrideItem,
  OrganisationConfigItem
} from '@futrli-sdk/consolidations';
import { clearWorkspacesListingQueryCache } from '@futrli/views/operations/workspaces/queries/workspacesListingQuery';
import { Currencies } from '@futrli-sdk/currencies';

import * as types from './types';

/**
 * Will create a new consolidation with the given data
 * @param {string} workspaceId
 * @param {types.CreateConsolidationParams} data
 */
export const createConsolidation = async (workspaceId: string, data: types.CreateConsolidationParams) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest<Consolidation>({
    payload: {
      request: {
        url: path,
        method: 'POST',
        data
      }
    }
  });

  clearWorkspacesListingQueryCache();

  return response.data;
};

/**
 * Will fetch consolidation settings data for the given id
 * @param {string} workspaceId
 * @param {string} consolidationId
 */
export const fetchConsolidation = async (workspaceId: string, consolidationId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId
  });
  const response = await APIClients.getAPIClient().makeRequest<Consolidation>({
    payload: {
      request: {
        url: path,
        method: 'GET'
      }
    }
  });

  return response.data;
};

/**
 * Will fetch the list of available consolidations
 * @param {string} workspaceId
 */
export const fetchConsolidations = async (workspaceId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest<Consolidation[]>({
    payload: {
      request: {
        url: path,
        method: 'GET'
      }
    }
  });

  return response.data;
};

/**
 * Fetches a combined chart of accounts for the given organisation ids.
 * @param {string} workspaceId
 * @param organisationIds - array of organisation ids
 */
export const fetchMergedCOA = async (workspaceId: string, organisationIds: string[]) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_COA, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest<ChartOfAccountBulkFlatItem[]>({
    payload: {
      request: {
        method: 'POST',
        data: JSON.stringify({ organisation_ids: organisationIds }),
        url: path
      }
    }
  });

  return response.data;
};

/**
 * Fetches a list of currencies that can be used as the base currency for a consolidation.
 */
export const fetchSupportedConsolidationCurrencies = async () => {
  const path = API_PATHS.FX_SUPPORTED_CURRENCIES;
  const response = await APIClients.getAPIClient().makeRequest<Currencies>({
    payload: {
      request: {
        url: path
      }
    }
  });

  return response.data.currencies;
};

/**
 * Fetches a list of FX rate overrides for the given consolidation
 * @param {string} workspaceId
 * @param {string} consolidationId
 */
export const fetchConsolidationFXOverrides = async (workspaceId: string, consolidationId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_FX_OVERRIDES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId
  });
  const response = await APIClients.getAPIClient().makeRequest<{ data: FxOverrideItem[] }>({
    payload: {
      request: {
        url: path
      }
    }
  });

  return response.data.data;
};

/**
 * Fetches the daily exchange rates for the given currencies between the given dates
 * @param baseCurrency - The currency to present the rates in E.G: USD
 * @param currencies - Array of currencies to fetch data for E.G: ['GBP', 'EUR']
 * @param startDate - Date to start from E.G: 2023/09/01
 * @param endDate - Date to end on E.G: 2024/09/01
 */
export const fetchFXRates = async (baseCurrency: string, currencies: string[], startDate: Date, endDate: Date) => {
  const path = API_PATHS.FX_HISTORICAL_RATES;
  const response = await APIClients.getAPIClient().makeRequest<types.FetchCurrencyRatesResponse>({
    payload: {
      request: {
        url: path,
        method: 'GET',
        params: {
          data_from: format(startDate, 'yyyy-MM-dd'),
          data_to: format(endDate, 'yyyy-MM-dd'),
          currency_code: currencies,
          currency_to: baseCurrency
        },
        paramsSerializer: params => {
          return queryString.stringify(params, { arrayFormat: 'none' });
        }
      }
    }
  });

  return response.data.data;
};

/**
 * Fetches the daily exchange rates for the given currencies between the given dates for the given consolidation (includes overrides)
 * @param currencies - Array of currencies to fetch data for E.G: ['GBP', 'USD']
 * @param startDate - Date to start from E.G: 2023/09/01
 * @param endDate - Date to end on E.G: 2024/09/01
 */
export const fetchConsolidationFXRates = async (
  workspaceId: string,
  consolidationId: string,
  currencies: string[],
  startDate: Date,
  endDate: Date
) => {
  const path = interpolatePathParams(API_PATHS.FX_HISTORICAL_RATES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId
  });
  const response = await APIClients.getAPIClient().makeRequest<types.FetchCurrencyRatesResponse>({
    payload: {
      request: {
        url: path,
        method: 'GET',
        params: {
          data_from: format(startDate, 'yyyy-MM-dd'),
          data_to: format(endDate, 'yyyy-MM-dd'),
          currency_code: currencies
        },
        paramsSerializer: params => {
          return queryString.stringify(params, { arrayFormat: 'none' });
        }
      }
    }
  });

  return response.data.data;
};

/**
 * Fetches some extra data for the list of available organisations.
 * @param {string} workspaceId
 */
export const fetchConsolidationOrganisationInfo = async (workspaceId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_USER_ORGS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest<OrganisationConfigItem[]>({
    payload: {
      request: {
        url: path
      }
    }
  });
  // const response = {
  //   data: mockOrganisations
  // };
  return response.data;
};

/**
 * Will update a consolidation with the given data
 * @param {string} workspaceId
 * @param {types.CreateConsolidationParams} data
 */
export const updateConsolidation = async (
  workspaceId: string,
  consolidationId: string,
  data: types.CreateConsolidationParams
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest<Consolidation>({
    payload: {
      request: {
        url: path,
        method: 'PUT',
        data
      }
    }
  });

  return response.data;
};

/* Deletes a consolidation
 * @param {string} workspaceId
 * @param {string} consolidationId
 */
export const deleteConsolidation = async (workspaceId: string, consolidationId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path,
        method: 'DELETE'
      }
    }
  });

  return response.data;
};
